import React, { useCallback, useId, useLayoutEffect, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn, initials } from "../../lib/utils";
import { Loader2, AlertTriangle, HelpCircle } from "lucide-react";

// The ONE button recipe — exported so link-shaped buttons (marketing ButtonLink) render from the
// same class maps instead of a hand-copied mirror that drifts.
export const BUTTON_VARIANTS = {
  primary: "bg-primary text-primary-fg hover:bg-primary-700 shadow-[0_4px_14px_rgba(29,78,216,0.25)]",
  saffron: "bg-saffron text-royal hover:brightness-95",  // dark text on saffron (white-on-saffron fails AA)
  secondary: "bg-surface text-accent-active border border-accent/30 hover:bg-accent/10",  // accent-active = AA-safe accent text (theme-aware)
  ghost: "bg-transparent text-ink-2 hover:bg-muted",
  danger: "bg-danger text-white hover:brightness-95",
  outline: "bg-surface text-ink-2 border border-line hover:bg-muted",
  dark: "bg-royal text-white hover:bg-navy",
  ai: "bg-ai text-white hover:bg-ai-dark shadow-[0_4px_14px_rgba(124,58,237,0.3)]",  // Aira AI (purple)
};
// RETIRED 2026-08-18 (WS6): `variant="sky"` — white-on-#0EA5E9 ≈2.77:1, an AA failure every former
// consumer was migrated off (SegmentPage/Help finish-review P1); repo-wide grep confirms zero live
// consumers. Kept as an ALIAS of `primary` so any stray/legacy `variant="sky"` renders the AA-safe
// primary recipe instead of resurrecting the failing fill. Do not pass "sky" in new code.
BUTTON_VARIANTS.sky = BUTTON_VARIANTS.primary;
// A Button is a product action, so every visual size keeps a comfortable 44px target. `sm`
// changes visual density (type/padding), not the hit area; `icon` is square by contract.
export const BUTTON_SIZES = {
  sm: "min-h-11 px-3 py-1.5 text-[13px]",
  md: "min-h-11 px-4 py-2.5 text-sm",
  lg: "min-h-12 px-6 py-3 text-[15px]",
  icon: "h-11 w-11 p-2.5",
};
export const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-[color,background-color,border-color,box-shadow,transform,filter] duration-200 active:scale-[0.98]";

export function Button({ variant = "primary", size = "md", className, children, loading, ...props }) {
  const variants = BUTTON_VARIANTS;
  const sizes = BUTTON_SIZES;
  return (
    <button
      // `{...props}` FIRST, then the computed attributes — the reverse order silently let any caller
      // that passed `disabled` overwrite `loading || props.disabled`, i.e. a busy button stayed
      // clickable and its double-submit guard was gone. 178 call sites pass both props, and the ones
      // that matter most are money actions ("Pay now" while a payment is in flight). Computing after
      // the spread makes `loading` un-disarmable: an explicit `disabled` can only ever ADD to it.
      {...props}
      data-ui={props["data-ui"] || "button"}
      className={cn(
        BUTTON_BASE,
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
        variants[variant],
        sizes[size],
        className
      )}
      disabled={loading || props.disabled}
      // `loading` stays un-disarmable (it forces aria-busy), but a caller that announces its OWN busy
      // state keeps it: computing `loading || undefined` here dropped an explicit `aria-busy` passed
      // through the spread. That is load-bearing for a control which must stay FOCUSABLE while its
      // request is in flight — ChatModeration's "Load earlier messages" walk-back cannot use `loading`
      // (that disables the button, and the browser blurs a control disabled mid-activation, dropping
      // the moderator's keyboard out of the dialog), so it passes `aria-busy` directly and needs the
      // explicit `false` rendered too. Only an absent caller value leaves the attribute off.
      aria-busy={loading ? true : props["aria-busy"]}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Card({ as: Component = "div", className, children, ...props }) {
  return (
    <Component
      className={cn("bg-surface rounded-2xl border border-line shadow-card", className)}
      {...props}
      data-ui={props["data-ui"] || "card"}
    >
      {children}
    </Component>
  );
}

const errorRing = "border-danger focus:border-danger focus:ring-danger/20";

const isAriaInvalid = (value) => value === true || value === "true";

// Finance forms frequently need to move focus to the first invalid field after server-side
// validation. Keep these primitives ref-safe and make both supported invalid contracts (`error`
// and `aria-invalid`) render the same visible state.
export const Input = React.forwardRef(function Input(
  {
    className,
    error,
    leadingIcon: LeadingIcon,
    "aria-invalid": ariaInvalid,
    ...props
  },
  ref
) {
  const invalid = Boolean(error) || isAriaInvalid(ariaInvalid);
  const control = (
    <input
      ref={ref}
      aria-invalid={error ? true : ariaInvalid}
      className={cn("input-base", LeadingIcon && "pl-9", invalid && errorRing, className)}
      {...props}
      data-ui={props["data-ui"] || "input"}
    />
  );
  if (!LeadingIcon) return control;
  return (
    <span className="relative block min-w-0" data-ui="input-shell">
      <LeadingIcon
        className="pointer-events-none absolute left-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-ink-subtle"
        aria-hidden="true"
      />
      {control}
    </span>
  );
});

export const Textarea = React.forwardRef(function Textarea(
  { className, error, "aria-invalid": ariaInvalid, ...props },
  ref
) {
  const invalid = Boolean(error) || isAriaInvalid(ariaInvalid);
  return (
    <textarea
      ref={ref}
      aria-invalid={error ? true : ariaInvalid}
      className={cn("input-base min-h-[96px] resize-y", invalid && errorRing, className)}
      {...props}
      data-ui={props["data-ui"] || "textarea"}
    />
  );
});

export function Label({ className, children, ...props }) {
  return (
    <label className={cn("label-base", className)} {...props} data-ui={props["data-ui"] || "label"}>
      {children}
    </label>
  );
}

export const Select = React.forwardRef(function Select(
  { className, children, error, "aria-invalid": ariaInvalid, ...props },
  ref
) {
  const invalid = Boolean(error) || isAriaInvalid(ariaInvalid);
  return (
    <select
      ref={ref}
      aria-invalid={error ? true : ariaInvalid}
      className={cn("input-base appearance-none select-chevron bg-surface cursor-pointer", invalid && errorRing, className)}
      {...props}
      data-ui={props["data-ui"] || "select"}
    >
      {children}
    </select>
  );
});

// Theme-aware tones (light values ≈ the old -50 tints; dark gets translucent fills with light text
// that hold APCA+WCAG2 on a dark surface). AI chips use the theme-aware soft/ink pair so the Aira
// identity remains readable in light, dark, high-contrast and sepia modes.
const badgeTones = {
  gray: "bg-muted text-ink-2 border-line",
  neutral: "bg-muted text-ink-2 border-line",
  blue: "bg-accent/12 text-accent-active border-accent/25",
  accent: "bg-accent/12 text-accent-active border-accent/25",
  sky: "bg-informative-soft text-informative border-informative/20",
  informative: "bg-informative-soft text-informative border-informative/20",
  green: "bg-positive-soft text-positive border-positive/20",
  positive: "bg-positive-soft text-positive border-positive/20",
  red: "bg-critical-soft text-critical border-critical/20",
  critical: "bg-critical-soft text-critical border-critical/20",
  amber: "bg-caution-soft text-caution border-caution/20",
  caution: "bg-caution-soft text-caution border-caution/20",
  teal: "bg-sarovar-500/12 text-positive border-sarovar-500/25",
  purple: "bg-ai-soft text-ai-ink border-ai/20",
  ai: "bg-ai-soft text-ai-ink border-ai/20",
  orange: "bg-orange-soft text-orange-dark border-orange-border/60",
  warning: "bg-caution-soft text-caution border-caution/20",
};

export function Badge({ tone = "gray", className, children, dot, ...props }) {
  return (
    <span
      {...props}
      data-ui={props["data-ui"] || "badge"}
      data-tone={tone}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[12px] font-semibold tracking-tight",
        badgeTones[tone] || badgeTones.gray,
        className
      )}
    >
      {dot && <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />}
      {children}
    </span>
  );
}

// Gender-aware fallback: with no photo we show initials on a tinted gradient — blue for male, rose
// for female, the brand blue otherwise. Initials keep the person identifiable; "other"/unknown is
// never mis-gendered (stays brand blue).
const _GENDER_GRAD = { male: "from-sky to-primary", female: "from-rose-500 to-pink-600" };
function _genderKey(g) {
  const s = String(g || "").trim().toLowerCase();
  if (["m", "male", "boy", "man"].includes(s)) return "male";
  if (["f", "female", "girl", "woman"].includes(s)) return "female";
  return null;
}
// P1 file-safety-scan audit: a photo src that failed to load (423 while its safety scan is
// pending, 404/410 stale reference) must NOT be re-fetched on every re-mount — HR/teacher rosters
// render 100+ avatars and each failed <img> GET is a console error, so one page view produced a
// ~160-request storm with no backoff. Failures are remembered per src for the session (module-level
// TTL map, the <img> here being the ONE shared fetch site): within the TTL the initials fallback
// renders with zero network; after it, one retry — so a photo whose scan has since completed
// appears without a reload.
const _failedPhotoSrc = new Map(); // src -> ms timestamp of the last load failure
const _FAILED_PHOTO_TTL_MS = 5 * 60 * 1000;
function _photoSrcRecentlyFailed(src) {
  const at = _failedPhotoSrc.get(src);
  if (at == null) return false;
  if (Date.now() - at >= _FAILED_PHOTO_TTL_MS) {
    _failedPhotoSrc.delete(src);
    return false;
  }
  return true;
}
function _rememberPhotoSrcFailure(src) {
  if (_failedPhotoSrc.size > 800) _failedPhotoSrc.clear(); // bound the per-session cache
  _failedPhotoSrc.set(src, Date.now());
}
export function Avatar({ name, src, gender, className, size = 38 }) {
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [src]);
  if (src && !imageFailed && !_photoSrcRecentlyFailed(src)) {
    return (
      <img
        src={src}
        alt={name || ""}
        onError={() => {
          _rememberPhotoSrcFailure(src);
          setImageFailed(true);
        }}
        className={cn("rounded-full object-cover bg-muted", className)}
        style={{ width: size, height: size }}
      />
    );
  }
  const grad = _GENDER_GRAD[_genderKey(gender)] || "from-primary to-sky";
  return (
    <div
      className={cn("rounded-full bg-gradient-to-br text-white grid place-items-center font-semibold font-heading", grad, className)}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
      role="img"
      aria-label={name ? `${name} avatar` : "Profile avatar"}
    >
      {initials(name) || "•"}
    </div>
  );
}

export function Spinner({ className }) {
  return <Loader2 className={cn("w-5 h-5 animate-spin text-primary", className)} />;
}

// Rich KPI tile for detail pages — icon in a tinted circle + label + value + optional hint. Keeps
// stat strips consistent + visually rich across every detail/360 page.
const _STAT_ACCENT = {
  primary: ["bg-accent/10", "text-accent-active"],
  success: ["bg-positive-soft", "text-positive"],
  saffron: ["bg-caution-soft", "text-caution"],
  ai: ["bg-ai-soft", "text-ai-ink"],
  sky: ["bg-informative-soft", "text-informative"],
  danger: ["bg-critical-soft", "text-critical"],
  slate: ["bg-muted", "text-ink-2"],
};
export function StatTile({ icon: Icon, label, value, hint, tone, accent = "primary", compact = false, onClick, testid, wrapLabel = false, wrapValue = false }) {
  const [bg, fg] = _STAT_ACCENT[accent] || _STAT_ACCENT.primary;
  const content = (
    <>
      <div className={cn("flex items-center", compact ? "gap-2" : "gap-3")}>
        {Icon && !compact && (
          <span className={cn("w-10 h-10 rounded-xl grid place-items-center shrink-0", bg)}>
            <Icon className={cn("w-5 h-5", fg)} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <p className={cn("text-ink-subtle uppercase tracking-wide", wrapLabel ? "whitespace-normal break-words" : "truncate", compact ? "text-[10px]" : "text-[11px]")}>{label}</p>
          {/* A clipped ₹ figure is a misread risk — the title always carries the full value
              (the same contract KpiCard's value node keeps). */}
          <p title={typeof value === "string" ? value : undefined} className={cn("font-heading font-bold leading-tight tabular-nums", wrapValue ? "whitespace-normal break-words" : "truncate", compact ? "text-[15px]" : "text-xl", tone || "text-ink")}>{value}</p>
        </div>
      </div>
      {hint && <p className={cn("text-ink-subtle", compact ? "mt-1 text-[10px]" : "mt-2 text-[11px]")}>{hint}</p>}
    </>
  );
  if (onClick) {
    return <Card className="p-0"><button type="button" onClick={onClick} className={cn("w-full rounded-xl text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", compact ? "p-2.5" : "p-4")} data-testid={testid}>{content}</button></Card>;
  }
  return <Card className={compact ? "p-2.5" : "p-4"} data-testid={testid}>{content}</Card>;
}

// Premium "featured icon" chip — a Lucide glyph in a tinted rounded square with a soft ring, the
// Untitled-UI/Stripe pattern. Colored ONLY via the semantic accent tokens (never raw hex) so it
// re-themes with white-label + finance/AI scopes. Reuses StatTile's `_STAT_ACCENT` fills.
// (Grafted from dev during the dev→guptadev merge so dev's pages that import FeaturedIcon build.)
const _CHIP_RING = {
  primary: "ring-accent/20", success: "ring-positive/25", saffron: "ring-caution/30",
  ai: "ring-ai/20", sky: "ring-informative/25", danger: "ring-critical/25", slate: "ring-line",
  celebrate: "ring-orange-border/60",
};
const _CHIP_SIZE = {
  sm: ["w-8 h-8 rounded-lg", "w-4 h-4"],
  md: ["w-10 h-10 rounded-xl", "w-5 h-5"],
  lg: ["w-12 h-12 rounded-2xl", "w-6 h-6"],
};
export function FeaturedIcon({ icon: Icon, accent = "primary", size = "md", ring = true, className }) {
  const [bg, fg] = _STAT_ACCENT[accent] || _STAT_ACCENT.primary;
  const [box, ic] = _CHIP_SIZE[size] || _CHIP_SIZE.md;
  return (
    <span className={cn("grid place-items-center shrink-0", box, bg, ring && "ring-1", ring && (_CHIP_RING[accent] || _CHIP_RING.primary), className)}>
      {Icon && <Icon className={cn(ic, fg)} aria-hidden="true" />}
    </span>
  );
}

// UX-002/064: skeleton placeholders to replace spinner-in-the-void loading (no layout shift).
export function Skeleton({ className }) {
  // Directional shimmer sweep (Stripe/Linear "actively fetching" read) instead of the generic
  // 2s opacity throb; motion-reduce falls back to the static block.
  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-lg bg-muted",
        "before:absolute before:inset-0 before:-translate-x-full motion-safe:before:animate-[shimmer_1.8s_ease-in-out_infinite]",
        "before:bg-gradient-to-r before:from-transparent before:via-white/50 before:to-transparent dark:before:via-white/10",
        className,
      )}
      aria-hidden="true"
    />
  );
}

// A convenience block: N stacked skeleton lines (e.g. table rows / list items).
export function SkeletonText({ lines = 3, className }) {
  return (
    <div className={cn("space-y-2", className)} role="status" aria-busy="true" aria-label="Loading">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={cn("h-4", i === lines - 1 ? "w-2/3" : "w-full")} />
      ))}
    </div>
  );
}

// UX-064/071: a designed error state with a retry path — the counterpart to EmptyState.
export function ErrorState({ title = "We couldn't load this section", desc, onRetry, retryLabel = "Try again" }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6" role="alert">
      <div className="w-16 h-16 rounded-2xl bg-red-50 grid place-items-center mb-4">
        <AlertTriangle className="w-8 h-8 text-danger" />
      </div>
      <h3 className="font-heading font-semibold text-ink-2 text-lg">{title}</h3>
      <p className="text-ink-subtle text-sm mt-1 max-w-sm">
        {desc || "Something went wrong while loading this content. Please try again."}
      </p>
      {onRetry && (
        <div className="mt-5">
          <Button variant="outline" onClick={onRetry}>{retryLabel}</Button>
        </div>
      )}
    </div>
  );
}

// WA5.2: accessible field help. The sighted UI gets one compact `?` trigger instead of a permanent
// paragraph under every control. The same guidance remains in the DOM as an sr-only description so
// a Field can reference it with aria-describedby even while the popover is closed.
//
// `help` may be a string or {help, why, example, learnMoreUrl}. `hint` is accepted separately so the
// migration from the legacy visible hint contract does not discard a richer help specification.
function normaliseGuidanceValue(value) {
  return typeof value === "string" ? value.trim() || null : value || null;
}

export function hasFieldGuidance(help, hint) {
  const suppliedSpec =
    typeof help === "string" || React.isValidElement(help) ? { help } : (help || {});
  return Boolean(
    normaliseGuidanceValue(hint) ||
    normaliseGuidanceValue(suppliedSpec.help) ||
    normaliseGuidanceValue(suppliedSpec.why) ||
    normaliseGuidanceValue(suppliedSpec.example)
  );
}

function fixedPositionContainingBlock(start) {
  let node = start;
  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const establishesFixedBlock =
      (style.transform && style.transform !== "none") ||
      (style.perspective && style.perspective !== "none") ||
      (style.filter && style.filter !== "none") ||
      (style.backdropFilter && style.backdropFilter !== "none") ||
      /\b(layout|paint|strict|content)\b/.test(style.contain || "") ||
      /\b(transform|perspective|filter)\b/.test(style.willChange || "");
    if (establishesFixedBlock) return node;
    node = node.parentElement;
  }
  return null;
}

export function InfoButton({
  label,
  help,
  hint,
  why,
  example,
  learnMoreUrl,
  descriptionId,
  className,
}) {
  const [open, setOpen] = useState(false);
  const generatedId = useId();
  const panelId = `${generatedId}-popover`;
  const accessibleDescriptionId = descriptionId || `${generatedId}-description`;
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimerRef = useRef(null);
  const pointerTypeRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState(null);
  const suppliedSpec =
    typeof help === "string" || React.isValidElement(help) ? { help } : (help || {});
  const spec = {
    help: normaliseGuidanceValue(suppliedSpec.help),
    why: normaliseGuidanceValue(suppliedSpec.why ?? why),
    example: normaliseGuidanceValue(suppliedSpec.example ?? example),
    learnMoreUrl: normaliseGuidanceValue(suppliedSpec.learnMoreUrl ?? learnMoreUrl),
  };
  const normalisedHint = normaliseGuidanceValue(hint);
  const body = spec.help ?? normalisedHint;
  const additionalHint = normalisedHint && normalisedHint !== body ? normalisedHint : null;
  const interactive = Boolean(spec.learnMoreUrl);
  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);
  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, [cancelScheduledClose]);
  const resolvePortalHost = useCallback(() => {
    if (typeof document === "undefined") return null;
    // A focusable help dialog must be a DOM descendant of the focus scope that launched it. Custom
    // Modal and Radix-backed drawers both expose role=dialog on that scope; body remains the correct
    // host for ordinary page fields.
    return btnRef.current?.closest('[role="dialog"]') || document.body;
  }, []);
  const positionPanel = useCallback(() => {
    const trigger = btnRef.current;
    if (!trigger || typeof window === "undefined") return;
    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 16;
    const gap = 8;
    const width = Math.min(288, Math.max(0, window.innerWidth - viewportPadding * 2));
    const measuredHeight = panelRef.current?.offsetHeight || 180;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openAbove = spaceBelow < Math.min(measuredHeight, 240) && spaceAbove > spaceBelow;
    const unclampedTop = openAbove
      ? rect.top - measuredHeight - gap
      : rect.bottom + gap;
    const viewportLeft = Math.max(
      viewportPadding,
      Math.min(rect.left, window.innerWidth - width - viewportPadding),
    );
    const viewportTop = Math.max(
      viewportPadding,
      Math.min(unclampedTop, window.innerHeight - measuredHeight - viewportPadding),
    );
    // A transformed modal/drawer establishes a containing block for fixed descendants. Translate
    // the viewport-clamped coordinates into that block so collision behavior stays correct after
    // moving the portal inside the parent focus scope.
    const containingBlock =
      fixedPositionContainingBlock(panelRef.current?.parentElement) ||
      (
        panelRef.current?.offsetParent &&
        panelRef.current.offsetParent !== document.body
          ? panelRef.current.offsetParent
          : null
      );
    const containingRect =
      containingBlock && containingBlock !== document.body
        ? containingBlock.getBoundingClientRect()
        : null;
    setPanelPosition({
      left: containingRect
        ? viewportLeft - containingRect.left + containingBlock.scrollLeft
        : viewportLeft,
      top: containingRect
        ? viewportTop - containingRect.top + containingBlock.scrollTop
        : viewportTop,
      width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    positionPanel();
    window.addEventListener("resize", positionPanel);
    window.addEventListener("scroll", positionPanel, true);
    return () => {
      window.removeEventListener("resize", positionPanel);
      window.removeEventListener("scroll", positionPanel, true);
    };
  }, [open, positionPanel]);

  useEffect(() => {
    return () => cancelScheduledClose();
  }, [cancelScheduledClose]);

  useEffect(() => {
    if (!open || !interactive) return;
    panelRef.current
      ?.querySelector("a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])")
      ?.focus();
  }, [interactive, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        // This is the active nested layer. Capture at Window and stop propagation so a parent
        // Modal/Radix dialog cannot observe the same Escape and dismiss itself first.
        event.stopPropagation();
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    const onPointerDown = (event) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(event.target) &&
        !btnRef.current?.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  if (!body && !additionalHint && !spec.why && !spec.example) return null;

  return (
    <span className={cn("relative inline-flex align-middle", className)}>
      <span id={accessibleDescriptionId} className="sr-only">
        {body}
        {additionalHint && <> {additionalHint}</>}
        {spec.why && <> Why: {spec.why}</>}
        {spec.example && <> Example: {spec.example}</>}
      </span>
      <button
        ref={btnRef}
        type="button"
        aria-label={label ? `Help for ${label}` : "More information"}
        aria-describedby={!interactive && open ? panelId : accessibleDescriptionId}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup={interactive ? "dialog" : undefined}
        onPointerDown={(event) => {
          pointerTypeRef.current = event.pointerType || "mouse";
        }}
        onClick={() => {
          const pointerType = pointerTypeRef.current;
          pointerTypeRef.current = null;
          cancelScheduledClose();
          setOpen((current) =>
            interactive || (pointerType && pointerType !== "mouse") ? !current : true
          );
        }}
        onFocus={() => {
          if (
            !interactive &&
            (!pointerTypeRef.current || pointerTypeRef.current === "mouse")
          ) {
            cancelScheduledClose();
            setOpen(true);
          }
        }}
        onBlur={() => {
          if (!interactive) scheduleClose();
        }}
        onMouseEnter={() => {
          if (!interactive) {
            cancelScheduledClose();
            setOpen(true);
          }
        }}
        onMouseLeave={() => {
          if (!interactive) scheduleClose();
        }}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-subtle",
          "transition-colors hover:bg-muted hover:text-accent-active",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
          "[@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11",
        )}
      >
        <HelpCircle className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={panelRef}
          id={panelId}
          role={interactive ? "dialog" : "tooltip"}
          aria-label={interactive ? (label ? `${label} help` : "Field help") : undefined}
          aria-describedby={interactive ? accessibleDescriptionId : undefined}
          tabIndex={interactive ? -1 : undefined}
          style={panelPosition || { left: 16, top: 16, width: 288, visibility: "hidden" }}
          onMouseEnter={!interactive ? cancelScheduledClose : undefined}
          onMouseLeave={!interactive ? scheduleClose : undefined}
          className={cn(
            "fixed z-[80] max-h-[calc(100vh-2rem)] overflow-y-auto",
            "rounded-xl border border-line bg-surface p-3 text-xs leading-relaxed text-ink-2 shadow-pop",
          )}
        >
          {body && <p>{body}</p>}
          {additionalHint && <p className="mt-1.5">{additionalHint}</p>}
          {spec.why && <p className="mt-1.5"><span className="font-medium text-ink">Why: </span>{spec.why}</p>}
          {spec.example && <p className="mt-1.5"><span className="font-medium text-ink">Example: </span>{spec.example}</p>}
          {spec.learnMoreUrl && (
            <a href={spec.learnMoreUrl} target="_blank" rel="noreferrer"
               className="mt-1.5 inline-block font-medium text-primary hover:underline">Learn more about this field</a>
          )}
        </div>,
        resolvePortalHost(),
      )}
    </span>
  );
}

// UX-008: label↔input association wrapper. Explanatory hint/help content is disclosed through an
// adjacent InfoButton; errors stay visible and are announced. The trigger is deliberately a sibling
// of <label> because interactive descendants inside a label produce ambiguous click/focus behaviour.
export function Field({ label, hint, error, required, help, className, children }) {
  const id = useId();
  const hasGuidance = hasFieldGuidance(help, hint);
  const guidanceId = hasGuidance ? `${id}-guidance` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const childCount = React.Children.count(children);
  const isCompositeGroup = React.isValidElement(children) && children.type === React.Fragment;
  const warnedAboutControlShape = useRef(false);
  if (
    process.env.NODE_ENV !== "production" &&
    !warnedAboutControlShape.current &&
    (childCount !== 1 || isCompositeGroup)
  ) {
    warnedAboutControlShape.current = true;
    console.warn(
      "Field expects exactly one control. Composite or multiple children cannot receive reliable label and aria-describedby wiring; wrap each control in its own Field."
    );
  }
  const childNodes = React.Children.toArray(children);
  const controlIndex = childNodes.findIndex((child) => React.isValidElement(child));
  const sourceControl = controlIndex >= 0 ? childNodes[controlIndex] : null;
  const isRequired = Boolean(required || sourceControl?.props?.required);
  const controlId = sourceControl?.props?.id || id;
  const renderedChildren = childNodes.map((child, index) => (
    index === controlIndex && React.isValidElement(child)
      ? React.cloneElement(child, {
          id: controlId,
          "aria-describedby": [child.props["aria-describedby"], guidanceId, errId].filter(Boolean).join(" ") || undefined,
          "aria-invalid": error ? true : child.props["aria-invalid"],
          "aria-required": isRequired || child.props["aria-required"],
          required: isRequired,
        })
      : child
  ));
  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <div className="flex min-h-8 items-center gap-0.5">
          <Label htmlFor={controlId} className="mb-0">
            {label}{isRequired && <span className="text-critical ml-0.5" aria-hidden="true">*</span>}
            {isRequired && <span className="sr-only"> (required)</span>}
          </Label>
          {hasGuidance && (
            <InfoButton
              label={typeof label === "string" ? label : undefined}
              help={help}
              hint={hint}
              descriptionId={guidanceId}
            />
          )}
        </div>
      )}
      {!label && hasGuidance && (
        <div className="flex justify-end">
          <InfoButton help={help} hint={hint} descriptionId={guidanceId} />
        </div>
      )}
      {renderedChildren}
      {error && <p id={errId} className="text-xs font-medium text-critical" role="alert">{error}</p>}
    </div>
  );
}

export function EmptyState({ icon: Icon, art, title, desc, action, compact = false, className }) {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center px-6", compact ? "py-7" : "py-16", className)}>
      {art ? (
        <img
          src={art}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className={cn("select-none drop-shadow-sm", compact ? "mb-3 h-12 w-12" : "mb-4 h-20 w-20")}
        />
      ) : Icon && (
        <div className={cn("rounded-2xl bg-primary-50 grid place-items-center", compact ? "mb-3 h-12 w-12" : "mb-4 h-16 w-16")}>
          <Icon className={cn("text-primary", compact ? "h-6 w-6" : "h-8 w-8")} aria-hidden="true" />
        </div>
      )}
      <h3 className={cn("font-heading font-semibold text-ink-2", compact ? "text-base" : "text-lg")}>{title}</h3>
      {desc && <p className="text-ink-subtle text-sm mt-1 max-w-sm">{desc}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Switch({ checked, onChange, disabled, className, "aria-label": ariaLabel, ...rest }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      {...rest}
      className={cn(
        "relative w-11 h-6 rounded-full transition-colors duration-fast shrink-0",
        "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none",
        disabled && "opacity-50 cursor-not-allowed",
        // bg-accent is var-backed — the checked state re-themes with white-label branding
        // (the hardcoded bg-sky ignored a tenant's brand accent).
        checked ? "bg-accent" : "bg-line-strong",
        className
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-fast ease-premium",
          checked && "translate-x-5"
        )}
      />
    </button>
  );
}

// Accessible checkbox — native input (so it's keyboard/AT-correct by default) styled to the tokens.
export function Checkbox({ checked, onChange, disabled, label, className, ...rest }) {
  // A LABELLED checkbox is already an easy target — the whole label is clickable. A BARE one (the
  // select-all in a table header, a row selector) was a 16x16 box, under the 24x24 WCAG 2.2 (2.5.8)
  // minimum and genuinely hard to hit on a phone. `p-1 -m-1` grows the hit area to 24x24 while the
  // negative margin cancels the padding, so nothing moves and the tick looks identical.
  return (
    <label className={cn("inline-flex items-center gap-2 text-sm text-ink-2", !label && "p-1 -m-1", disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer", className)}>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="w-4 h-4 rounded border-line text-primary accent-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 focus-visible:outline-none"
        {...rest}
      />
      {label && <span>{label}</span>}
    </label>
  );
}

// Accessible radio group — role=radiogroup + native radios; options = [{value,label}].
export function RadioGroup({ value, onChange, options = [], name, className, "aria-label": ariaLabel }) {
  const auto = useId();
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn("flex flex-col gap-2", className)}>
      {options.map((o) => (
        <label key={o.value} className="inline-flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
          <input
            type="radio"
            name={name || auto}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange?.(o.value)}
            className="w-4 h-4 border-line text-primary accent-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

// Lightweight tooltip — CSS-driven (hover + focus-within), wired via role=tooltip + aria-describedby
// on a single focusable child (no JS state, no portal).
export function Tooltip({ label, children, className }) {
  const id = useId();
  const child = React.isValidElement(children)
    ? React.cloneElement(children, { "aria-describedby": [children.props["aria-describedby"], id].filter(Boolean).join(" ") })
    : children;
  return (
    <span className={cn("relative inline-flex group/tt", className)}>
      {child}
      {/* Hover-intent: a 300ms delay before showing on pointer hover (no strobing when sweeping
          icon rows) with a 2px rise; keyboard focus shows instantly (delay-0 wins on focus). */}
      <span role="tooltip" id={id}
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 whitespace-nowrap rounded-lg bg-royal text-white text-[11px] px-2 py-1 opacity-0 translate-y-0.5 transition-[opacity,transform] duration-150 ease-premium group-hover/tt:opacity-100 group-hover/tt:translate-y-0 group-hover/tt:delay-300 group-focus-within/tt:opacity-100 group-focus-within/tt:translate-y-0 group-focus-within/tt:delay-0 motion-reduce:translate-y-0 motion-reduce:transition-none z-50">
        {label}
      </span>
    </span>
  );
}
